import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { ssoApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { SIGNUP_TOKEN_KEY } from './RegisterPage';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';

/**
 * Where Google and Microsoft drop people back.
 *
 * The URL carries a **single-use code**, not a session. ⚠️ That is the whole point: the old
 * Google callback put a working login token in the query string, which lands in browser
 * history, the referrer header and every proxy log in between. This code expires in five
 * minutes and does nothing until it is POSTed back.
 *
 * Two outcomes:
 *   • a brand new company  -> straight on to /welcome to name itself
 *   • an address that already has an account -> ⚠️ we do NOT sign them in silently. We say
 *     which company it belongs to and ask (design decision C). Somebody who clicked "sign
 *     up" may not have realised they already had an account, and somebody trying to start
 *     a new company with a work address deserves to be told why they can't.
 */
const SsoContinuePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const code = params.get('code');

  const [state, setState] = useState('working');   // working | existing | error
  const [account, setAccount] = useState(null);
  const [message, setMessage] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    // ⚠️ Guard against React StrictMode's deliberate double-invoke. The code is SINGLE-USE:
    // running this twice would spend it on the first call and fail on the second, which
    // would look exactly like an expired link to somebody who had just arrived.
    if (!code || ran.current) {
      if (!code) { setState('error'); setMessage('That sign-in link is missing its code.'); }
      return;
    }
    ran.current = true;

    ssoApi.exchange(code)
      .then((r) => {
        const data = r?.data || {};
        if (data.outcome === 'welcome') {
          // New company. The scoped token gets them through the /welcome gate and nothing
          // else — same key the password sign-up uses, so /welcome needs no special case.
          sessionStorage.setItem(SIGNUP_TOKEN_KEY, data.signup_token);
          navigate('/welcome', { replace: true, state: { email: data.email } });
          return;
        }
        setAccount(data);
        setState('existing');
      })
      .catch((e) => {
        setState('error');
        setMessage(e.response?.data?.detail
          || 'That sign-in link has expired. Please try again.');
      });
  }, [code, navigate]);

  const handleContinue = async () => {
    setSigningIn(true);
    try {
      const r = await ssoApi.complete(code);
      loginWithToken(r.data);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      // ⚠️ The refusals from the login gate arrive here too — a closed account, a
      // deactivated company, an unpaid subscription. Signing in with Google must not walk
      // past the doors a password has to go through, so this can legitimately fail.
      toast.error(e.response?.data?.detail || 'Could not sign you in. Please try again.');
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <BrandedToaster />
      <ThemeToggle iconOnly variant="ghost" className="fixed top-4 right-4 z-50 text-muted-foreground hover:text-foreground" />

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size={34} />
        </div>

        <div className="card-hive p-8">
          {state === 'working' && (
            <div className="py-6 text-center text-muted-foreground">
              <Loader2 className="w-7 h-7 mx-auto mb-4 animate-spin text-primary" />
              Signing you in…
            </div>
          )}

          {state === 'existing' && (
            <div data-testid="sso-existing">
              <h1 className="font-heading text-2xl font-bold mb-3">You already have an account</h1>
              <p className="text-muted-foreground mb-6">
                <span className="font-semibold text-foreground">{account?.email}</span> is
                already part of{' '}
                <span className="font-semibold text-foreground">
                  {account?.company_name || 'a company on HolidayHive'}
                </span>
                {account?.display_name ? <>, as {account.display_name}</> : null}.
              </p>
              <Button
                className="btn-primary w-full mb-3"
                onClick={handleContinue}
                disabled={signingIn}
                data-testid="sso-continue-btn"
              >
                {signingIn
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in…</>
                  : `Continue to ${account?.company_name || 'your account'}`}
              </Button>
              {/* ⚠️ A real way out, not just a back button. Somebody who meant to start a
                  NEW company with this address needs to understand why they can't, and
                  what to do instead.

                  ⚠️ AND IT DELIBERATELY DOES NOT GO BACK TO THE PROVIDER. Two different
                  people press this: one wants a different Microsoft account, one wants
                  nothing to do with Microsoft. The sign-in screen serves both — the
                  provider picker is one click away and always appears now, and email,
                  password and the other provider are right there. Sending them back to
                  Microsoft serves only the first, and would land the habitual clicker on
                  the same account and the same screen: a loop that reads as a broken
                  button.

                  ⚠️ The label says "another way", not "a different account", because the
                  latter reads as "a different MICROSOFT account" — which is the one thing
                  it doesn't do (Rob spotted the ambiguity, 16/08/2026). */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login', { replace: true, state: { ssoBackedOut: true } })}
                data-testid="sso-different-btn"
              >
                Sign in another way
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                Want a separate company on HolidayHive? Sign up with a different email
                address — each address can belong to one company.
              </p>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="sso-error">
              <h1 className="font-heading text-2xl font-bold mb-3">That didn't work</h1>
              <p className="text-muted-foreground mb-6">{message}</p>
              <Button className="btn-primary w-full" onClick={() => navigate('/login', { replace: true })}>
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SsoContinuePage;
